import { Link } from "react-router-dom";
import { Clock, HardHat, MapPin, Users } from "lucide-react";
import type { DashboardData } from "@crewledger/shared/schemas";
import { CommandCenterCard, SectionHeading, StatCard } from "@/components/design";
import { Button } from "@/components/ui/button";
import { PayPeriodBanner } from "@/features/dashboard/components/pay-period-banner";
import { QuickActions } from "@/features/dashboard/components/quick-actions";
import { formatCurrency, formatHours } from "@/features/dashboard/utils";

interface MiniCommandCenterProps {
  data: DashboardData | null;
  loading: boolean;
}

/** Real workspace snapshot — only surfaces metrics when the account has operational data. */
export function MiniCommandCenter({ data, loading }: MiniCommandCenterProps) {
  const currency = data?.currency ?? "USD";
  const kpis = data?.kpis;
  const hasHours = Boolean(kpis && kpis.totalHoursToday > 0);
  const hasPayPeriod = Boolean(data?.currentPayPeriod);
  const hasSiteActivity = Boolean(data?.siteActivity?.some((site) => site.totalHours > 0));
  const hasOperationalData = hasHours || hasPayPeriod || hasSiteActivity;

  if (loading) {
    return (
      <CommandCenterCard title="Workspace snapshot" description="Loading your live workspace data…">
        <p className="text-sm text-muted-foreground">Pulling real counts from your company only.</p>
      </CommandCenterCard>
    );
  }

  if (!hasOperationalData) {
    return (
      <CommandCenterCard
        title="Workspace snapshot"
        description="Your command center will light up as you add sites, workers, and entries."
      >
        <p className="text-sm text-muted-foreground">
          No labor hours or pay periods yet — complete the setup checklist to start tracking real
          field data. Demo examples stay in the separate demo workspace.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/sites/new">
            <Button size="sm" variant="outline">
              <MapPin className="mr-2 h-4 w-4" />
              Add a site
            </Button>
          </Link>
          <Link to="/workers/new">
            <Button size="sm" variant="outline">
              <Users className="mr-2 h-4 w-4" />
              Add workers
            </Button>
          </Link>
        </div>
      </CommandCenterCard>
    );
  }

  return (
    <div className="space-y-6">
      <SectionHeading
        title="Workspace snapshot"
        description="Live data from your beta workspace — not demo seed data"
      />

      {hasPayPeriod ? (
        <PayPeriodBanner
          payPeriod={data?.currentPayPeriod ?? null}
          asOfDate={data?.asOfDate}
          loading={false}
        />
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {hasHours ? (
          <StatCard
            label="Hours today"
            value={formatHours(kpis!.totalHoursToday)}
            icon={Clock}
            accent="orange"
          />
        ) : null}
        {kpis && kpis.activeWorkers > 0 ? (
          <StatCard
            label="Active workers"
            value={String(kpis.activeWorkers)}
            icon={Users}
            accent="navy"
          />
        ) : null}
        {data && data.siteActivity.length > 0 ? (
          <StatCard
            label="Active sites"
            value={String(data.siteActivity.length)}
            icon={MapPin}
            accent="navy"
          />
        ) : null}
        {kpis && kpis.estimatedGrossPay != null && kpis.estimatedGrossPay > 0 ? (
          <StatCard
            label="Est. gross pay"
            value={formatCurrency(kpis.estimatedGrossPay, currency)}
            icon={HardHat}
            accent="orange"
          />
        ) : null}
      </div>

      {hasSiteActivity ? (
        <CommandCenterCard title="Site activity today" description="Hours logged across your job sites">
          <ul className="space-y-2 text-sm">
            {data!.siteActivity
              .filter((site) => site.totalHours > 0)
              .slice(0, 4)
              .map((site) => (
                <li key={site.siteId} className="flex items-center justify-between gap-3">
                  <span className="font-medium">{site.siteName}</span>
                  <span className="text-muted-foreground">{formatHours(site.totalHours)}</span>
                </li>
              ))}
          </ul>
        </CommandCenterCard>
      ) : null}

      <QuickActions />
    </div>
  );
}
